import type {
  PublicSupplierProfile,
  SortOrder,
  SupplierDetailResponse,
  SupplierDirectoryListResponse,
  SupplierDirectorySortField
} from '@tandemone/types'

import { api } from '@/api/client'
import { csvParam } from '@/lib/csv-param'

export type SupplierDirectoryListParams = {
  founding: boolean
  verified: boolean
  favorites: boolean
  search?: string
  countries: string[]
  capabilityGroups: string[]
  industries: string[]
  certifications: string[]
  page: number
  pageSize: number
  sort?: SupplierDirectorySortField
  order?: SortOrder
}

function listQuery(params: SupplierDirectoryListParams) {
  return {
    founding: params.founding || undefined,
    verified: params.verified || undefined,
    favorites: params.favorites || undefined,
    search: params.search || undefined,
    countries: csvParam(params.countries),
    capabilityGroups: csvParam(params.capabilityGroups),
    industries: csvParam(params.industries),
    certifications: csvParam(params.certifications),
    page: params.page,
    pageSize: params.pageSize,
    sort: params.sort,
    order: params.order
  }
}

export async function listSuppliers(
  params: SupplierDirectoryListParams,
  signal?: AbortSignal
): Promise<SupplierDirectoryListResponse> {
  const { data } = await api.get<SupplierDirectoryListResponse>(
    '/v1/suppliers',
    { params: listQuery(params), signal }
  )
  return data
}

export async function listPublicSuppliers(
  params: SupplierDirectoryListParams,
  signal?: AbortSignal
): Promise<SupplierDirectoryListResponse> {
  const { data } = await api.get<SupplierDirectoryListResponse>(
    '/v1/public/suppliers',
    { params: listQuery(params), signal }
  )
  return data
}

export async function getPublicSupplier(
  slug: string,
  signal?: AbortSignal
): Promise<PublicSupplierProfile> {
  const { data } = await api.get<PublicSupplierProfile>(
    `/v1/public/suppliers/${slug}`,
    { signal }
  )
  return data
}

export async function getSupplier(
  slug: string,
  signal?: AbortSignal
): Promise<SupplierDetailResponse> {
  const { data } = await api.get<SupplierDetailResponse>(
    `/v1/suppliers/${slug}`,
    { signal }
  )
  return data
}

export type SupplierDocumentDownload = {
  previewUrl: string
  downloadUrl: string
  expiresAt: string
}

export async function getSupplierDocumentDownload(
  slug: string,
  documentId: string,
  signal?: AbortSignal
): Promise<SupplierDocumentDownload> {
  const { data } = await api.get<SupplierDocumentDownload>(
    `/v1/suppliers/${slug}/documents/${documentId}/download`,
    { signal }
  )
  return data
}

export async function favoriteSupplier(slug: string): Promise<void> {
  await api.post(`/v1/suppliers/${slug}/favorite`)
}

export async function unfavoriteSupplier(slug: string): Promise<void> {
  await api.delete(`/v1/suppliers/${slug}/favorite`)
}
